"use client";

import { useIsMobile } from "@nimble/react-state-hooks/use-mobile";
import { APIProvider } from "@vis.gl/react-google-maps";
import {
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { LocationDropdownView } from "@/components/shared/location-dropdown/location-dropdown.view";
import { getSuggestionsList } from "@/components/shared/location-dropdown/suggestions-list";
import { useLocationDropdown } from "@/components/shared/location-dropdown/use-location-dropdown";
import { publicEnv } from "@/env/public-env";

export interface LocationDropdownProps {
  value?: string;
  onChange: (value: string) => void;
  topCities: readonly string[];
  topCitiesLabel?: string;
  placeholder: string;
  dataTestId?: string;
  useStrictAddress: boolean;
  recentSearches: readonly string[];
}

export function LocationDropdownInner({
  value,
  onChange,
  topCities,
  topCitiesLabel,
  placeholder,
  useStrictAddress,
  recentSearches,
  dataTestId,
}: LocationDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();
  const [searchTerm, setSearchTerm] = useState(value || "");

  // Syncs with external value
  useEffect(() => {
    const next = value || "";

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSearchTerm((prev) => {
      if (prev === next) return prev;
      return next;
    });
  }, [value]);

  const setOpen = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        setSearchTerm(value || "");
      }

      setIsOpen(newOpen);
    },
    [value],
  );

  const { handleSelect, fetchPredictions, predictions, isResolvingLocation } =
    useLocationDropdown({
      onChange,
      setOpen,
      useStrictAddress,
    });

  const suggestions = useMemo(() => {
    return isOpen
      ? getSuggestionsList({
          topCities,
          topCitiesLabel,
          value,
          searchTerm,
          handleSelect,
          recentSearches,
          isResolvingLocation,
          predictions,
        })
      : null;
  }, [
    topCities,
    topCitiesLabel,
    value,
    searchTerm,
    handleSelect,
    recentSearches,
    isResolvingLocation,
    predictions,
    isOpen,
  ]);

  const onSearchTermChange = useCallback(
    (val: string) => {
      setSearchTerm(val);
      fetchPredictions(val);
      setOpen(true);
    },
    [fetchPredictions, setSearchTerm, setOpen],
  );

  const clearSearch = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setSearchTerm("");
      onChange("");
    },
    [onChange, setSearchTerm],
  );

  return (
    <LocationDropdownView
      open={isOpen}
      setOpen={setOpen}
      placeholder={placeholder}
      searchTerm={searchTerm}
      onSearchTermChange={onSearchTermChange}
      clearSearch={clearSearch}
      suggestions={suggestions}
      dataTestId={dataTestId}
      isMobile={isMobile}
    />
  );
}

export default function LocationDropdown(props: LocationDropdownProps) {
  return (
    <APIProvider apiKey={publicEnv.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY ?? ""}>
      <LocationDropdownInner {...props} value={props.value || ""} />
    </APIProvider>
  );
}
